import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Fab,
  Snackbar,
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSkills } from '../hooks';
import { SkillTable, SkillForm } from '../components';
import type { CreateSkillDto, Skill, UpdateSkillDto } from '@/types/domain';

/**
 * 技能管理页面
 * 展示技能列表并支持创建、编辑、删除操作
 */
export default function Skills() {
  const { t } = useTranslation();
  const {
    skills,
    loading,
    error,
    success,
    refresh,
    create,
    update,
    delete: deleteSkill,
    clearMessages,
    setError,
  } = useSkills();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState<Skill | null>(null);

  // 打开创建对话框
  const handleOpenCreate = () => {
    setEditingSkill(null);
    clearMessages();
    setDialogOpen(true);
  };

  // 打开编辑对话框
  const handleEdit = (skill: Skill) => {
    setEditingSkill(skill);
    clearMessages();
    setDialogOpen(true);
  };

  // 关闭对话框
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSkill(null);
    clearMessages();
  };

  // 处理删除（打开确认对话框）
  const handleDelete = (skill: Skill) => {
    setSkillToDelete(skill);
    setDeleteConfirmOpen(true);
  };

  // 确认删除
  const confirmDelete = async () => {
    if (!skillToDelete) {
      return;
    }

    try {
      await deleteSkill(skillToDelete.id);
      setDeleteConfirmOpen(false);
      setSkillToDelete(null);
    } catch {
      // 错误已在 hook 中处理
    }
  };

  // 处理表单提交
  const handleSubmit = async (data: CreateSkillDto | UpdateSkillDto) => {
    if (editingSkill) {
      await update(editingSkill.id, data);
      if (!error) {
        handleCloseDialog();
      }
    } else {
      await create(data as CreateSkillDto);
      if (!error) {
        handleCloseDialog();
      }
    }
  };

  return (
    <Box>
      {/* 页面标题和操作栏 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">{t('skills.title')}</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => void refresh()}
          disabled={loading}
        >
          {t('skills.refresh')}
        </Button>
      </Box>

      {/* 错误提示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearMessages}>
          {error}
        </Alert>
      )}

      {/* 技能列表表格 */}
      <SkillTable
        skills={skills}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* 创建按钮 */}
      <Fab
        color="primary"
        aria-label={t('skills.createSkill')}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
        onClick={handleOpenCreate}
      >
        <AddIcon />
      </Fab>

      {/* 创建/编辑对话框 */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' },
        }}
      >
        <DialogTitle>
          {editingSkill ? t('skills.editTitle') : t('skills.createTitle')}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <SkillForm
            mode={editingSkill ? 'edit' : 'create'}
            initialData={editingSkill || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCloseDialog}
            onError={setError}
          />
        </DialogContent>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('skills.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('skills.deleteConfirm', { name: skillToDelete?.name })}</Typography>
          {skillToDelete?.builtin && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('skills.builtinDeleteWarning')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={() => void confirmDelete()}
            color="error"
            variant="contained"
            disabled={!!skillToDelete?.builtin}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 成功提示 */}
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={clearMessages}
        message={success}
      />
    </Box>
  );
}
